
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { PaginatedSearchQueryParams } from "@/store/types";
import { Getter } from "vuex-class";
import axios, { CancelTokenSource } from "axios";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import { debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import api from "@/services/api.service";

@Component({
	components: {
		"v-select-3": vSelect3,
	},
})
export default class ContactGroupSelect extends Vue {
	@Prop({ type: Boolean, default: false })
	private readOnly: boolean;

	@Prop({ type: Array, required: true, default: [] })
	private value: ContactGroup[];

	@Prop({ type: String, required: false, default: "Contact Groups" })
	private label: string;

	@Prop({ type: Boolean, required: false })
	private required: boolean;

	@Prop({ type: String, required: false, default: "Search for Contact Groups" })
	private placeholder: string;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	private contactGroupSearchToken: CancelTokenSource = null;
	private contactGroups: ContactGroup[] = [];
	private selectedContactGroups: ContactGroup[] = [];

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	private async mounted(): Promise<void> {
		await this.retrieveContactGroups();
		this.setSelectedContactGroups();
	}

	@Watch("value")
	private onValueChange(): void {
		this.selectedContactGroups = this.value;
		this.setSelectedContactGroups();
	}

	private handleSearchInput = debounce(
		async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	private async retrieveContactGroups(searchTerm: string = "", page: number = 1, pageSize: number = 10): Promise<void> {
		if (!this.isContactGroupsEnabled) {
			return;
		}

		const params: PaginatedSearchQueryParams = {
			page: page,
			pageSize: pageSize,
			sortBy: "title",
			searchTerm: searchTerm,
		};

		this.contactGroupSearchToken = axios.CancelToken.source();

		const request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token,
		};

		try {
			this.contactGroups = (await api.getContactGroups(request)).data;
		} catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message,
			});
			return;
		}
	}

	private setSelectedContactGroups(): void {
		if (this.value && this.value.length > 0 && this.contactGroups) {
			const newGroups = this.contactGroups.filter((cg: ContactGroup) =>
				this.value.map((v) => v.contactGroupId).includes(cg.contactGroupId)
			);
			const combined = [...this.selectedContactGroups, ...newGroups];

			// Only use distinct Contact Groups
			this.selectedContactGroups = Array.from(new Map(combined.map((group) => [group.contactGroupId, group])).values());
		} else {
			this.selectedContactGroups = [];
		}
	}

	private onSearch(searchTerm: string): void {
		this.handleSearchInput(searchTerm);
	}

	@Emit("input")
	private onValueUpdated(contactGroups: ContactGroup[]): ContactGroup[] {
		this.selectedContactGroups = contactGroups;
		return contactGroups;
	}
}
